import { useEffect, useMemo, useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { customFetch } from "@workspace/api-client-react";
import { NOME_DO_TURNO, faixaDoTurnoTexto, type HorarioDosTurnos, type Turno } from "@workspace/pricing";
import { formatAvailabilityDateLong } from "@/lib/availability";
import {
  type CapacidadeDoCarrinho,
  type ItemDeEncomenda,
  conflitoDeDias,
  dataCurta,
  hojeNaLoja,
  horariosDoCarrinho,
  inicioDoCarrinho,
  limiteDoCarrinho,
  modoDoCarrinho,
  motivoDoDia,
  prazoDoCarrinho,
  turnosDoCarrinho,
} from "@/lib/encomendaUnica";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const pad = (n: number) => String(n).padStart(2, "0");
const mesDe = (ds: string) => ({ y: Number(ds.slice(0, 4)), m0: Number(ds.slice(5, 7)) - 1 });

// Data única no carrinho (loja com orderSingleDate): UMA data e UM horário para o pedido inteiro, escolhidos no
// checkout. O calendário é o do carrinho (lib/encomendaUnica): prazo mais longo entre os itens, dias que todos
// aceitam, capacidade de cada produto e a vaga de "uma encomenda por horário". Com turno ou só a data (12/10/2026)
// vale o modo mais detalhado entre os produtos do carrinho.
export function EncomendaDoPedido({
  slug,
  unitId,
  itens,
  horarios,
  data,
  hora,
  turno,
  onChange,
  destaque,
  cardRef,
}: {
  slug: string;
  unitId: string | null;
  itens: ItemDeEncomenda[];
  horarios: HorarioDosTurnos;
  data: string | null;
  hora: string | null;
  turno: Turno | null;
  onChange: (data: string | null, hora: string | null, turno: Turno | null) => void;
  destaque: boolean;
  cardRef: RefObject<HTMLDivElement | null>;
}) {
  const chaveItens = itens.map((i) => `${i.productId}:${i.quantidade}`).join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prazo = useMemo(() => prazoDoCarrinho(itens), [chaveItens]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const conflito = useMemo(() => conflitoDeDias(itens, new Date(), 60, horarios), [chaveItens, horarios]);
  const modo = modoDoCarrinho(itens);
  const hoje = hojeNaLoja();
  // O calendário abre no mês do primeiro dia aceito: o prazo ou o início da encomenda, o que vier depois.
  const [mes, setMes] = useState(() => {
    const de = inicioDoCarrinho(itens, null)?.data ?? null;
    const base = prazo?.data ?? hoje;
    return mesDe(data ?? (de && de > base ? de : base));
  });
  const ids = [...new Set(itens.map((i) => i.productId))].sort().join(",");
  const { data: cap } = useQuery<CapacidadeDoCarrinho>({
    queryKey: ["order-capacity-cart", slug, unitId, ids, mes.y, mes.m0],
    queryFn: () =>
      customFetch(`/api/catalog/${slug}/order-capacity-cart?ids=${ids}&month=${mes.m0 + 1}&year=${mes.y}${unitId ? `&unitId=${encodeURIComponent(unitId)}` : ""}`),
    enabled: itens.length > 0,
  });
  const capDoMes = cap ?? null;

  // Seleção que deixou de valer (carrinho mudou, vaga tomada, prazo andou): limpa e o cliente escolhe de novo.
  useEffect(() => {
    if (!data || !capDoMes || data.slice(0, 7) !== `${mes.y}-${pad(mes.m0 + 1)}`) return;
    if (motivoDoDia(itens, data, capDoMes, prazo, horarios)) {
      onChange(null, null, null);
      return;
    }
    if (hora && (modo !== "horario" || !horariosDoCarrinho(itens, data, capDoMes, prazo, {}, horarios).includes(hora))) onChange(data, null, turno);
    if (turno && (modo !== "turno" || !turnosDoCarrinho(itens, data, capDoMes, prazo, horarios).includes(turno))) onChange(data, hora, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, hora, turno, capDoMes, chaveItens, prazo, modo]);

  const diasNoMes = new Date(mes.y, mes.m0 + 1, 0).getDate();
  const primeiroDiaSemana = new Date(mes.y, mes.m0, 1).getDay();
  const mesAtual = mesDe(hoje);
  // «Aceitar encomendas» de/até (26/10/2026): vale o início mais tarde e o fim mais cedo entre os itens; as setas não
  // passam dos meses deles.
  const inicio = inicioDoCarrinho(itens, capDoMes);
  const limite = limiteDoCarrinho(itens, capDoMes);
  const mesMostrado = `${mes.y}-${pad(mes.m0 + 1)}`;
  const voltarDesabilitado = (mes.y === mesAtual.y && mes.m0 <= mesAtual.m0) || (!!inicio && inicio.data.slice(0, 7) >= mesMostrado);
  const avancarDesabilitado = !!limite && limite.data.slice(0, 7) <= mesMostrado;
  const janelasDiferentes = new Set(itens.map((i) => `${i.de ?? ""}|${i.ate ?? ""}`)).size > 1;
  const horariosDoDia = data && modo === "horario" ? horariosDoCarrinho(itens, data, capDoMes, prazo, {}, horarios) : [];
  const turnosDoDiaEscolhido = data && modo === "turno" ? turnosDoCarrinho(itens, data, capDoMes, prazo, horarios) : [];
  const prazosDiferentes = new Set(itens.map((i) => (i.minAdvanceHours ? `h${i.minAdvanceHours}` : `d${i.minAdvanceDays}`))).size > 1;
  // Com o início da encomenda depois do prazo, a data do prazo não diz nada (o primeiro dia é o do início).
  const textoPrazo =
    prazo && (prazo.data > hoje || prazo.hora) && !(inicio && inicio.data > prazo.data)
      ? ` Prazo: a partir de ${dataCurta(prazo.data)}${prazo.hora ? ` às ${prazo.hora}` : ""}${prazosDiferentes ? ` (${prazo.quem})` : ""}.`
      : "";
  const comQuem = (j: { data: string; quem: string }) => `${dataCurta(j.data)}${janelasDiferentes ? ` (${j.quem})` : ""}`;
  // Sem dia em comum (o início mais tarde depois do fim mais cedo), quem explica é o aviso de conflito abaixo.
  const textoLimite = inicio && limite && inicio.data > limite.data
    ? ""
    : inicio && limite
    ? ` Encomendas de ${comQuem(inicio)} a ${comQuem(limite)}.`
    : inicio ? ` Encomendas a partir de ${comQuem(inicio)}.` : limite ? ` Encomendas até ${comQuem(limite)}.` : "";
  const pedeTexto = modo === "turno" ? "a data e o turno" : modo === "data" ? "a data" : "a data e o horário";

  return (
    <div
      ref={cardRef}
      data-testid="checkout-encomenda"
      className={`bg-card p-5 rounded-2xl shadow border space-y-4 transition-all ${destaque ? "border-red-400 ring-2 ring-red-300" : "border-border/50"}`}
    >
      <h2 className="font-bold text-lg flex items-center gap-2">
        <CalendarDays className="w-5 h-5 text-secondary" /> Quando você quer a encomenda?
      </h2>
      {destaque && <p className="text-sm text-red-600 font-medium -mt-2">Escolha {pedeTexto} para continuar.</p>}
      <p className="text-xs text-muted-foreground -mt-2">Vale para o pedido inteiro.{textoPrazo}{textoLimite}</p>

      {conflito ? (
        <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-2" data-testid="checkout-encomenda-conflito">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Estes itens não têm um dia em comum ({conflito}). Remova um deles ou faça pedidos separados.</span>
        </div>
      ) : (
        <>
          <div className="border border-border/50 rounded-xl p-3">
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => setMes(mes.m0 === 0 ? { y: mes.y - 1, m0: 11 } : { y: mes.y, m0: mes.m0 - 1 })}
                disabled={voltarDesabilitado}
                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                aria-label="Mês anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-semibold text-sm">{MESES[mes.m0]} {mes.y}</span>
              <button
                type="button"
                onClick={() => setMes(mes.m0 === 11 ? { y: mes.y + 1, m0: 0 } : { y: mes.y, m0: mes.m0 + 1 })}
                disabled={avancarDesabilitado}
                className="p-1 rounded hover:bg-muted disabled:opacity-30"
                aria-label="Próximo mês"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {DIAS.map((d) => <div key={d} className="text-xs font-medium text-muted-foreground py-1">{d}</div>)}
              {Array.from({ length: primeiroDiaSemana }).map((_, i) => <div key={`v${i}`} />)}
              {Array.from({ length: diasNoMes }).map((_, i) => {
                const dia = i + 1;
                const ds = `${mes.y}-${pad(mes.m0 + 1)}-${pad(dia)}`;
                const motivo = ds < hoje ? "passou" : motivoDoDia(itens, ds, capDoMes, prazo, horarios);
                const selecionado = data === ds;
                return (
                  <button
                    key={dia}
                    type="button"
                    disabled={!!motivo}
                    title={motivo && motivo !== "passou" ? motivo : undefined}
                    onClick={() => onChange(ds, null, null)}
                    data-testid={`encomenda-dia-${ds}`}
                    className={`w-full aspect-square rounded-lg text-sm font-medium transition-all
                      ${selecionado ? "bg-accent text-accent-foreground font-bold" : ""}
                      ${!selecionado && !motivo ? "hover:bg-accent/10" : ""}
                      ${motivo ? "text-muted-foreground/30 cursor-not-allowed" : "cursor-pointer"}
                      ${ds === hoje && !selecionado ? "ring-1 ring-primary/50" : ""}`}
                  >
                    {dia}
                  </button>
                );
              })}
            </div>
          </div>

          {modo === "data" && (
            <p className="text-xs text-muted-foreground" data-testid="encomenda-so-data">O horário você combina com a loja pelo WhatsApp.</p>
          )}

          {data && modo === "turno" && (
            <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <h3 className="font-bold text-sm">Turno</h3>
                <span className="text-xs text-muted-foreground">{formatAvailabilityDateLong(data)}</span>
              </div>
              {turnosDoDiaEscolhido.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum turno disponível para esta data.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {turnosDoDiaEscolhido.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => onChange(data, null, t)}
                      data-testid={`encomenda-turno-${t}`}
                      className={`py-2 px-2 rounded-lg border transition-all flex flex-col items-center gap-0.5
                        ${turno === t ? "bg-accent text-accent-foreground border-accent" : "border-border hover:bg-accent/10 hover:border-accent/30"}`}
                    >
                      <span className="text-sm font-semibold">{NOME_DO_TURNO[t]}</span>
                      <span className={`text-[11px] ${turno === t ? "text-accent-foreground/90" : "text-muted-foreground"}`}>{faixaDoTurnoTexto(horarios[t])}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {data && modo === "horario" && (
            <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <h3 className="font-bold text-sm">Horário</h3>
                <span className="text-xs text-muted-foreground">{formatAvailabilityDateLong(data)}</span>
              </div>
              {horariosDoDia.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum horário disponível para esta data.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {horariosDoDia.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => onChange(data, t, null)}
                      data-testid={`encomenda-hora-${t}`}
                      className={`py-2 rounded-lg text-sm font-medium transition-all border
                        ${hora === t ? "bg-accent text-accent-foreground border-accent" : "border-border hover:bg-accent/10 hover:border-accent/30"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
